import PlotlyChart from './PlotlyChart'
import { prepareLineChartData, resolveColumnRef } from '../utils/chartDataUtils'

/**
 * LineChartV2 - A line chart component with support for derived columns and series grouping
 *
 * @param {Object} props
 * @param {Array} props.data - Raw data array from the analytics query
 * @param {Object} props.dimensionInfo - Dimension lookup data (e.g., { clientId: [{ id: 1, name: 'Client A' }] })
 * @param {Object} props.chartOptions - Chart configuration options
 * @param {Array} props.chartOptions.columns - Unified column definitions (shared with Table)
 * @param {string|Object} props.chartOptions.x - X-axis column reference (id, source string, or full definition)
 * @param {string|Object} props.chartOptions.y - Y-axis column reference (or array for multiple y axes)
 * @param {string|Object} props.chartOptions.seriesBy - Column reference to create series by
 * @param {string} props.chartOptions.title - Chart title
 * @param {Object} props.chartOptions.layout - Additional Plotly layout options
 * @param {Object} props.chartOptions.config - Additional Plotly config options
 *
 * Column definition format:
 * {
 *   id: string,                 // Optional identifier for referencing this column
 *   source: string | function,  // Property name, dot notation (e.g., 'clientId.name'), or (row, dimensionInfo) => value
 *   label: string,              // Display label for axis/legend
 *   kind: 'date' | 'number',    // Value type
 *   format: 'percentage' | 'currency' | 'number' | function,  // Value formatting
 *   dateFormat: string,         // date-fns format string for dates
 *   decimalPlaces: number       // Decimal places for number formatting
 * }
 *
 * Example usage with unified columns:
 * <LineChartV2
 *   data={data}
 *   dimensionInfo={dimensionInfo}
 *   chartOptions={{
 *     title: 'Pickup Rate by Client',
 *     columns: [
 *       { id: 'clientName', source: 'clientId.name', label: 'Client Name' },
 *       { id: 'date', source: 'time', kind: 'date', label: 'Date' },
 *       { id: 'pickupRate', source: (row) => row.count > 0 ? row.countAnswered / row.count : 0, format: 'percentage', label: 'Pickup Rate' }
 *     ],
 *     x: 'date',
 *     y: 'pickupRate',
 *     seriesBy: 'clientName'
 *   }}
 * />
 */
export default function LineChartV2({ data, chartOptions = {}, dimensionInfo = {} }) {
  const {
    columns = [],
    x = { source: 'time', kind: 'date', label: 'Time' },
    y = { source: 'count', label: 'Count' },
    seriesBy,
    title,
    layout: customLayout = {},
    config: customConfig = {}
  } = chartOptions

  // Resolve the x column for layout configuration
  const xColumn = resolveColumnRef(x, columns)

  // Support multiple y columns
  const yRefs = Array.isArray(y) ? y : [y]

  // If we have multiple y columns and no seriesBy, create a trace per y column
  let allTraces = []
  let yAxisConfig = {}

  if (yRefs.length > 1 && !seriesBy) {
    // Multiple metrics mode - one trace per y column
    for (const yRef of yRefs) {
      const yColumn = resolveColumnRef(yRef, columns)
      const { traces, yAxisConfig: yConfig } = prepareLineChartData({
        data,
        dimensionInfo,
        columns,
        x,
        y: yRef,
        seriesBy: null
      })

      // Override trace name with y column label
      for (const trace of traces) {
        trace.name = yColumn.label || yColumn.source
        allTraces.push(trace)
      }
      yAxisConfig = yConfig
    }
  } else {
    // Single y column or seriesBy mode
    const { traces, yAxisConfig: yConfig } = prepareLineChartData({
      data,
      dimensionInfo,
      columns,
      x,
      y: yRefs[0],
      seriesBy
    })
    allTraces = traces
    yAxisConfig = yConfig
  }

  // Build layout
  const layout = {
    title,
    autosize: true,
    xaxis: {
      title: xColumn.label,
      ...(xColumn.kind === 'date' ? { type: 'date' } : {})
    },
    yaxis: yAxisConfig,
    legend: {
      orientation: 'h',
      yanchor: 'bottom',
      y: 1.02,
      xanchor: 'right',
      x: 1
    },
    margin: { t: title ? 60 : 30, r: 30, b: 50, l: 60 },
    hovermode: 'closest',
    ...customLayout
  }

  const config = {
    displayModeBar: false,
    responsive: true,
    ...customConfig
  }

  return (
    <PlotlyChart
      data={allTraces}
      useResizeHandler={true}
      layout={layout}
      config={config}
      style={{ width: '100%', height: '400px' }}
    />
  )
}
